<template>
  <!-- Level-up / Reward-unlocked modal (always mounted, visibility via notification prop) -->
  <RewardCelebrationModal
    :notification="mappedRewardNotification"
    :child="currentChild"
    @dismiss="handleDismiss"
  />

  <!-- Chest modal (always mounted, visibility via is-open for proper Ionic dismiss).
       Deux chemins d'ouverture : la notification (célébration à chaud) et la
       bloc de la page Récompenses (stock durable). Le second existe justement parce que
       le premier peut disparaître sans que le coffre ait été ouvert. -->
  <ChestModal
    :is-open="isChestOpen"
    :chest="activeChest"
    :is-onboarding="isFakeChest"
    @update:is-open="handleChestClose"
    @later="handleChestLater"
    @claim-complete="handleChestClaimed"
  />

  <!-- Bilan de la semaine. Même schéma que le coffre : une carte trop riche pour
       le gabarit titre + message de RewardCelebrationModal, donc son propre
       composant, alimenté par la même file. -->
  <WeeklyRecapCelebration
    :is-open="isRecapOpen"
    :recap="notifiedRecap"
    :child="currentChild ?? undefined"
    :child-view="!!appContextStore.childViewChildId"
    @dismiss="handleRecapDismiss"
  />

  <!-- L'encouragement d'un parent : réservé à une vue enfant (audience). -->
  <CheerCelebration
    :cheer="notifiedCheer"
    :child="currentChild"
    @dismiss="handleCheerDismiss"
  />
</template>

<script setup lang="ts">
import { computed, watch, ref, onMounted, onUnmounted } from 'vue'
import RewardCelebrationModal from './RewardCelebrationModal.vue'
import ChestModal from '@/components/chest/ChestModal.vue'
import { playSound } from '@/lib/sound'
import WeeklyRecapCelebration from '@/components/dashboard/WeeklyRecapCelebration.vue'
import CheerCelebration, { type CheerPayload } from '@/components/cheers/CheerCelebration.vue'
import { useNotificationQueue, setChildAudienceActive } from '@/composables/useNotificationQueue'
import { useAppContextStore } from '@/stores/appContext'
import { useChestsStore } from '@/stores/chests'
import { childService } from '@/services/child.service'
import { chestService } from '@/services/chest.service'
import { weeklyRecapService } from '@/services/weeklyRecap.service'
import type { Child } from '@/models/Child'
import type { Chest } from '@/models/Chest'
import type { WeeklyRecap } from '@/models/WeeklyRecap'
import type { RewardNotification } from '@/composables/useRewardNotification'
import { i18n } from '@/i18n'

const t = (key: string, params?: Record<string, unknown>) => i18n.global.t(key, params)
const locale = computed(() => i18n.global.locale.value as string)

const {
  currentNotification,
  fetchPending,
  fetchPendingHousehold,
  unsubscribe,
  dismissCurrent,
  dismissAllOfType,
  skipCurrent,
  completeAction,
  switchChild,
  switchHousehold,
  getDisplayMessage,
} = useNotificationQueue()

const appContextStore = useAppContextStore()
const chestsStore = useChestsStore()

// -----------------------------------------------------------------------
// Child data for the modal
// -----------------------------------------------------------------------
const currentChild = ref<Child | null>(null)

watch(currentNotification, async (notification) => {
  const childId = notification?.child_id
  // La modale s'ouvre dans le même rendu : lui donner tout de suite l'enfant
  // déjà en mémoire. Attendre la relecture réseau faisait sortir les premières
  // images aux couleurs par défaut et sans prénom (« Bien joué, » puis
  // « Bien joué Léa, » une fraction de seconde plus tard).
  const known = childId ? appContextStore.availableChildren.find((c) => c.id === childId) : null
  currentChild.value = known ?? appContextStore.currentChild ?? null
  if (!childId) return
  try {
    const child = await childService.getChildById(childId)
    if (child) currentChild.value = child
  } catch {
    // On garde l'enfant en mémoire.
  }
})

// -----------------------------------------------------------------------
// Chest data. Deux sources, une seule modale :
//  - `notifiedChest` : le coffre porté par une notification `chest_available` ;
//  - `chestsStore.requestedChest` : celui que l'utilisateur a demandé depuis la
//    bloc de la page Récompenses.
// La demande explicite l'emporte : elle vient d'un tap, la notification non.
// -----------------------------------------------------------------------
const notifiedChest = ref<Chest | null>(null)

const openedFromPill = computed(() => !!chestsStore.requestedChest)
const activeChest = computed<Chest | null>(() => chestsStore.requestedChest ?? notifiedChest.value)

// Visibility controlled by prop (not v-if) so Ionic can run dismiss animation
const isChestOpen = computed(() => !!activeChest.value)

// Coffre injecté par le panel debug (pushFakeNotification) : pas d'appel API
// au claim (ChestModal le saute quand is-onboarding est vrai).
const isFakeChest = computed(
  () => !openedFromPill.value && !!notifiedChest.value && !!currentNotification.value?.id?.startsWith('fake-')
)

watch(currentNotification, async (notification) => {
  // Debug/QA : une fake notification (DebugNotificationTriggers) embarque le
  // coffre complet dans le payload — pas de lookup DB, claim court-circuité
  // via is-onboarding sur ChestModal.
  if (notification?.type === 'chest_available' && notification.payload?.chest) {
    notifiedChest.value = notification.payload.chest as Chest
    return
  }
  if (notification?.type === 'chest_available' && notification.payload?.chest_id) {
    try {
      const childId = notification.child_id
      if (childId) {
        const unclaimed = await chestService.getUnclaimedChests(childId)
        const found = unclaimed.find(c => c.id === notification.payload.chest_id) ?? null
        notifiedChest.value = found

        if (found) {
          // Le stock durable apprend le coffre en même temps que la modale :
          // si l'utilisateur ferme sans réclamer, la pilule prend le relais.
          chestsStore.noteGenerated(found)
        } else {
          // Chest already claimed (e.g. by another device) — auto-dismiss
          console.log('[NotificationQueue] Chest already claimed, auto-dismissing')
          chestsStore.markClaimed(notification.payload.chest_id)
          await dismissCurrent()
        }
      }
    } catch (err) {
      // Lecture en échec (réseau) : on PASSE sans supprimer. Supprimer ici
      // revenait à perdre le coffre à cause d'une coupure, puisque la
      // notification était son seul chemin d'accès.
      console.error('[NotificationQueue] Error loading chest:', err)
      notifiedChest.value = null
      skipCurrent()
    }
  } else {
    notifiedChest.value = null
  }
})

// -----------------------------------------------------------------------
// Bilan de la semaine. La notification est posée par la base au moment où le
// bilan devient 'generated' (trigger trg_notify_weekly_recap), et ressort ici
// à la relance de l'app via fetchPending — c'est tout l'intérêt de passer par
// cette file plutôt que d'inventer un déclencheur au boot.
// -----------------------------------------------------------------------
const notifiedRecap = ref<WeeklyRecap | null>(null)
const isRecapOpen = computed(() => !!notifiedRecap.value)

watch(currentNotification, async (notification) => {
  if (notification?.type !== 'weekly_recap') {
    notifiedRecap.value = null
    return
  }

  // Debug/QA : le panneau de déclencheurs embarque le bilan complet dans le
  // payload, comme il le fait pour le coffre — sinon il faudrait attendre un
  // dimanche 19h pour voir cette célébration une fois.
  if (notification.payload?.recap) {
    notifiedRecap.value = notification.payload.recap as WeeklyRecap
    return
  }

  const recapId = notification.payload?.recap_id as string | undefined
  if (!recapId) {
    await dismissCurrent()
    return
  }

  try {
    const recap = await weeklyRecapService.getRecapById(recapId)
    // Ligne disparue (enfant supprimé, foyer quitté) : il n'y a rien à montrer
    // et il n'y aura jamais rien. On retire la notification pour de bon.
    if (!recap) {
      await dismissCurrent()
      return
    }
    notifiedRecap.value = recap
  } catch (err) {
    // Lecture en échec (réseau) : on PASSE sans supprimer, comme pour le coffre.
    // Supprimer ici perdrait la célébration à cause d'une coupure.
    console.error('[NotificationQueue] Error loading weekly recap:', err)
    notifiedRecap.value = null
    skipCurrent()
  }
})

// -----------------------------------------------------------------------
// Encouragement d'un parent. Tout est dans le payload, posé par la base.
// -----------------------------------------------------------------------
const notifiedCheer = computed<CheerPayload | null>(() => {
  const n = currentNotification.value
  if (n?.type !== 'parent_cheer') return null
  return n.payload as CheerPayload
})

function handleCheerDismiss() {
  void dismissCurrent()
}

// -----------------------------------------------------------------------
// Map DB notification to the RewardNotification interface expected by
// RewardCelebrationModal (backward-compatible adapter)
// -----------------------------------------------------------------------
const mappedRewardNotification = computed<RewardNotification | null>(() => {
  const n = currentNotification.value
  // Les deux types qui ont leur propre surface : sans ce garde-fou, la modale
  // générique s'ouvrirait PAR-DESSUS avec un message vide.
  if (!n || n.type === 'chest_available' || n.type === 'weekly_recap' || n.type === 'parent_cheer') return null

  const base = { id: n.id, childId: n.child_id! }

  switch (n.type) {
    case 'level_up':
      return {
        ...base,
        type: 'level',
        level: n.payload.level,
        oldLevel: n.payload.old_level,
      }
    case 'reward_unlocked':
      return {
        ...base,
        type: 'reward',
        reward: {
          title: n.payload.reward_title,
        },
      }
    case 'admin':
      return {
        ...base,
        type: 'custom',
        // Map payload.title → reward.title so the modal's titleMain picks
        // it up; payload.message becomes the subtitle. Falls back to a
        // single string if only one of the two is present.
        reward: n.payload.title
          ? { title: n.payload.title, description: n.payload.message }
          : undefined,
        message: n.payload.message ?? n.payload.title ?? '',
      }
    case 'streak_milestone': {
      const days = n.payload.days
      const name = n.payload.child_name ?? ''
      const isChildMode = !!appContextStore.childViewChildId
      const title = isChildMode
        ? t('notifications.streakMilestone.childTitle', { days })
        : t('notifications.streakMilestone.parentTitle', { name, days })
      const message = isChildMode
        ? t('notifications.streakMilestone.childBody')
        : t('notifications.streakMilestone.parentBody', { name, days })
      return {
        ...base,
        type: 'custom',
        reward: { title },
        message,
      }
    }
    case 'allowance_credited': {
      const amount = Number(n.payload.amount ?? 0)
      const currency = n.payload.currency ?? 'EUR'
      const formatted = new Intl.NumberFormat(locale.value, { style: 'currency', currency }).format(amount)
      return { ...base, type: 'custom', reward: { title: t('allowance.title') }, message: t('allowance.notif.credited', { amount: formatted }) }
    }
    // Synthetic debug types — pass through as-is so the modal can render
    // each variant. Never emitted by the DB.
    case 'animal':
    case 'pose':
    case 'accessory':
    case 'background':
      return {
        ...base,
        type: n.type,
        item: n.payload.item,
        image: n.payload.image,
      }
    case 'task-completion':
      return {
        ...base,
        type: 'task-completion',
        pointsEarned: n.payload.pointsEarned,
        totalPoints: n.payload.totalPoints,
        taskName: n.payload.taskName,
      }
    default:
      return { ...base, type: 'custom', message: getDisplayMessage(n) }
  }
})

// Un palier de série sonne en s'ouvrant (lib/sound, coupé par le réglage).
watch(() => currentNotification.value?.id, () => {
  if (currentNotification.value?.type === 'streak_milestone') void playSound('streak')
})

// -----------------------------------------------------------------------
// Event handlers
// -----------------------------------------------------------------------
function handleDismiss() {
  dismissCurrent()
}

function handleChestClose(isOpen: boolean) {
  if (isOpen) return

  // Ouverture depuis la pilule : il n'y a aucune ligne à supprimer, on relâche
  // juste la demande. Le coffre reste dans le stock tant qu'il n'est pas ouvert.
  if (openedFromPill.value) {
    chestsStore.clearRequest()
    return
  }

  // Chemin notification : la célébration a été vue, on la retire. Le coffre, lui,
  // reste atteignable par la pilule tant qu'il n'est pas réclamé.
  notifiedChest.value = null
  dismissCurrent()
}

// « Plus tard » sur un coffre annoncé : toute la file des coffres part avec
// lui. Ils restent dans le stock, l'onglet Bonus les garde. Une ouverture
// demandée depuis le stock n'a rien à écarter.
function handleChestLater() {
  if (openedFromPill.value) return
  notifiedChest.value = null
  void dismissAllOfType('chest_available')
}

function handleRecapDismiss() {
  notifiedRecap.value = null
  void dismissCurrent()
}

async function handleChestClaimed() {
  const claimedId = activeChest.value?.id
  // Sort le coffre du stock sans aller-retour serveur : la pilule se décrémente
  // (et disparaît au dernier) dans le même geste que la réclamation.
  if (claimedId) chestsStore.markClaimed(claimedId)

  notifiedChest.value = null

  const notif = currentNotification.value
  if (!notif || notif.type !== 'chest_available') return
  const notificationId = notif.id // capture before any await
  await completeAction(notificationId)
}

// -----------------------------------------------------------------------
// Lifecycle: subscribe to the active child's notifications
// -----------------------------------------------------------------------
const activeChildId = computed(() => appContextStore.childViewChildId ?? appContextStore.currentChildId)

// Vue enfant (accès enfant ou mode enfant) : les célébrations qui lui sont
// réservées peuvent passer. Posé AVANT l'abonnement ci-dessous, qui les lit.
watch(() => !!appContextStore.childViewChildId, (on) => setChildAudienceActive(on), { immediate: true })

watch(
  [activeChildId, () => appContextStore.currentHouseholdId],
  ([childId, householdId]) => {
    if (childId) {
      switchChild(childId)
      // Stock de coffres de l'enfant qui entre en contexte. L'hydratation du
      // foyer l'a déjà chargé au boot ; ceci couvre les changements d'enfant et
      // les coffres apparus depuis (autre appareil, autre session).
      void chestsStore.loadForChild(childId)
    } else if (householdId) {
      // No single child active (all-kids page, parent dashboard): listen at the
      // household level so the parent still gets notifications for whichever
      // child just earned a reward. Watching currentHouseholdId here ensures we
      // (re)subscribe when the household resolves after a cold boot, even while
      // activeChildId stays null.
      switchHousehold(householdId)
    } else {
      unsubscribe()
    }
  },
  { immediate: true }
)

// Re-fetch on app resume (catches notifications created while in background)
function handleAppResumed() {
  if (activeChildId.value) {
    fetchPending(activeChildId.value)
    void chestsStore.loadForChild(activeChildId.value)
  } else if (appContextStore.currentHouseholdId) {
    fetchPendingHousehold(appContextStore.currentHouseholdId)
  }
}

onMounted(() => {
  window.addEventListener('app-resumed', handleAppResumed)
})

onUnmounted(() => {
  window.removeEventListener('app-resumed', handleAppResumed)
  unsubscribe()
})
</script>
